<template>
  <div class="order-details">
    <v-card class="card">
      <h2 class="order-summary__title">
        {{ $t("message.payments.invoiceData") }}
      </h2>
      <p class="order-details__invoice-description">
        {{ $t("message.payments.invoiceDataDescription") }}
      </p>
      <div class="payment-status">
        <v-form
          v-model="valid"
          :class="{ 'mt-0': $vuetify.breakpoint.smAndUp }"
        >
          <div class="payment-status__nip-field">
            <v-text-field
              :key="form.gov_tax_number.id"
              v-model="form.gov_tax_number.value"
              v-validate="{ required: form.gov_tax_number.required }"
              :data-vv-name="$t(form.gov_tax_number.label)"
              :error-messages="errors.collect($t(form.gov_tax_number.label))"
              :label="
                form.gov_tax_number.required
                  ? `${$t(form.gov_tax_number.label)} *`
                  : $t(form.gov_tax_number.label)
              "
              :required="form.gov_tax_number.required"
              :disabled="form.gov_tax_number.disabled"
            >
              <template #append>
                <v-tooltip :disabled="isNipValid" bottom max-width="250">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-btn
                        color="primary"
                        :disabled="!isNipValid"
                        text
                        @click="handleGetNipNumber"
                      >
                        {{ $t("message.payments.getData") }}
                      </v-btn>
                    </div>
                  </template>
                  <span>
                    {{ $t("message.payments.vatNumberRetrievalTooltip") }}
                  </span>
                </v-tooltip>
              </template>
            </v-text-field>
          </div>
          <v-text-field
            v-for="item in [
              form.legal_name,
              form.city,
              form.postal_code,
              form.street,
              form.country
            ]"
            :key="item.id"
            v-model="item.value"
            v-validate="{ required: item.required }"
            :data-vv-name="$t(item.label)"
            :error-messages="errors.collect($t(item.label))"
            :label="item.required ? `${$t(item.label)} *` : $t(item.label)"
            :required="item.required"
            :disabled="item.disabled"
          ></v-text-field>
        </v-form>
      </div>
      <div class="order-details__actions">
        <ui-button @click="$router.push({ name: 'order-details' })">{{
          $t("message.common.back")
        }}</ui-button>
        <ui-button
          class="order-details__go-next"
          color="primary"
          @click="submit"
        >
          {{ $t("message.common.further") }}
        </ui-button>
      </div>
    </v-card>
  </div>
</template>

<script>
import { companyProfileMixin } from "@/helpers/mixins/company-profile";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import serviceHandler from "@/helpers/handlers/service";
import companyService from "@/services/company";
import { AccountType } from "@/enums/account-type";

export default {
  name: "InvoiceData",
  mixins: [companyProfileMixin],
  data() {
    return {
      valid: false,
      localCompanyId: null
    };
  },
  computed: {
    isNipValid() {
      return this.validatePolishVAT(this.form.gov_tax_number.value);
    }
  },
  async mounted() {
    this.setForm();
  },
  async created() {
    if (
      !this.$store.state.payments.selectedPaymentMethod ||
      !this.$store.getters["payments/getPriceId"]
    ) {
      this.$router.push({ name: "order-details" });
    }
    this.$store.commit("company/SET_LOADING", true);
    await this.fetchCountries();
    await this.fetchCompanyProfileData();
    this.$store.commit("company/SET_LOADING", false);
  },
  methods: {
    async handleGetNipNumber(e) {
      await this.onTaxNumberSearch(e);
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.BILLING_DATA_LOADED_AUTOMATICALLY_FOR_TAX_ID
      });
    },
    validatePolishVAT(vatNumber) {
      // Remove any dashes and whitespace
      const cleaned = vatNumber.replace(/[-\s]/g, "");

      // Check if it's exactly 10 digits
      if (!/^\d{10}$/.test(cleaned)) {
        return false;
      }

      // Weights for validation algorithm
      const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7, 8];

      // Calculate sum
      let sum = 0;
      for (let i = 0; i < 9; i++) {
        sum += parseInt(cleaned[i]) * weights[i];
      }

      // Check if modulo 11 equals last digit
      const checkDigit = sum % 11;
      const lastDigit = parseInt(cleaned[9]);

      return checkDigit === lastDigit;
    },
    async fetchCompanyProfileData() {
      const data = await serviceHandler(companyService.getCompanies());
      await this.$store.dispatch("company/setCompanies", data);

      if (!data.length) return;

      const responseData = data.find(company => {
        return company.company_user.type === AccountType.OWNER;
      });
      if (!responseData) return;

      this.localCompanyId = responseData.id;
      await this.$store.dispatch("company/setCompany", {
        company: responseData
      });
      Object.entries(responseData).map(([k, v]) =>
        this.form[k] ? (this.form[k].value = v) : false
      );
    },
    submit() {
      this.$store.commit("company/SET_LOADING", true);

      this.$validator.validate().then(async isTrue => {
        if (isTrue) {
          await this.send();
          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.BILLING_DATA_PROVIDED
          });
          this.$router.push({ name: "checkout" });
        } else {
          this.$store.commit("company/SET_LOADING", false);
        }
      });
    },
    async send() {
      this.$store.commit("company/SET_LOADING", true);
      const formValues = {};
      this.form.categories.value = this.idFromObj(this.form.categories.value);

      Object.entries(this.form).map(([k, v]) => (formValues[k] = v.value));
      const [companyObject] = this.$store.state.company.companies;
      const { name } = companyObject;
      formValues.name = name;
      await this.updateCompanyData(formValues);

      this.$store.commit("company/SET_LOADING", false);
    },
    async updateCompanyData(formValues) {
      const data = await serviceHandler(
        companyService.updateCompany(formValues, this.localCompanyId)
      );

      if (data) {
        this.localCompanyId = data.id;
        await this.$store.dispatch("company/setCompanyId", data.id);
        await this.$store.dispatch("company/setCompany", {
          company: data
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.payment-status {
  &__nip-field {
    display: flex;
    gap: $spacing * 3;
    align-items: center;

    .v-btn {
      text-transform: none;
      letter-spacing: unset;
      font-weight: 600;
    }
  }
}
.card {
  padding: 3 * $spacing 4 * $spacing;
  display: flex;
  flex-direction: column;
  gap: $spacing * 3;
  height: auto;
  color: $dark-blue-3;
}
.header {
  display: flex;
  align-items: center;
  gap: $spacing * 2.5;

  &__title {
    @include font-size(30px);
  }
}

.order-summary {
  background: rgba($gray-300, 0.2);
  padding: $spacing * 2.5;
  display: flex;
  gap: $spacing * 3;
  flex-direction: column;
  border-radius: 7px !important;

  &__title {
    @include font-size(24px);
  }

  &__total-price {
    padding: $spacing * 3 0 $spacing * 0.5 0;
    border-top: 1px solid $gray-300;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @include font-size(20px);

    * {
      line-height: 1em;
    }

    span {
      @include font-size(14px);
    }
  }
}

.order-details {
  &__invoice-description {
    @include font-size(14px);
    color: $dark;
    margin: 0;
    padding: 0;
  }
  &__go-next,
  &__go-back {
  }

  &__go-next {
    align-self: flex-end;
  }

  &__go-back {
    align-self: flex-start;
  }

  &__actions {
    display: flex;
    gap: $spacing * 2;
    justify-content: space-between;
  }
}

.order-summary,
.order-checkout {
  width: 100%;
}
</style>
